import { useLocale } from './LocaleContext';
import { format } from './format';
import { messageRegistry } from './messages';
import en from './messages/en';
import type { TranslationKey } from './types';

type MessageTree = Record<string, unknown>;

function getNestedValue(obj: MessageTree, dotPath: string): string | undefined {
    const parts = dotPath.split('.');
    let current: unknown = obj;
    for (const part of parts) {
        if (typeof current !== 'object' || current === null) return undefined;
        current = (current as MessageTree)[part];
    }
    return typeof current === 'string' ? current : undefined;
}

/**
 * Returns a `t()` function scoped to the active locale.
 *
 * - In development: throws if a key is missing from the active locale's
 *   messages, to catch omissions early.
 * - In production: falls back to English, then returns the key itself.
 */
export function useTranslation(): {
    t: (key: TranslationKey, vars?: Record<string, string | number>) => string;
} {
    const locale = useLocale();
    const messages = messageRegistry[locale] as MessageTree;

    function t(
        key: TranslationKey,
        vars?: Record<string, string | number>
    ): string {
        const value = getNestedValue(messages, key);

        if (value === undefined) {
            if (process.env.NODE_ENV === 'development') {
                throw new Error(
                    `Missing translation key: "${key}" for locale "${locale}"`
                );
            }
            const fallback =
                getNestedValue(en as unknown as MessageTree, key) ?? key;
            return vars ? format(fallback, vars) : fallback;
        }

        return vars ? format(value, vars) : value;
    }

    return { t };
}
